import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { ReactSortable } from "react-sortablejs";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Swal from "sweetalert2";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";

const TherapistReorderModal = ({
  open,
  handleClose,
  therapists,
  dayId,
  refreshData,
}) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (therapists && open) {
      const sorted = [...therapists].sort((a, b) => a.sortOrder - b.sortOrder);
      setList(sorted);
    }
  }, [therapists, open]);

  const handleSaveOrder = async () => {
    setLoading(true);
    try {
      const req = list.map((item, index) => ({
        id: item.id,
        therapistId: item.therapistId,
        newOrder: index,
      }));

      const res = await AppointmentsApi.UpdateTherapistsOrder(req);

      if (res.status === "Success") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Order updated!",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        await refreshData(dayId);
        handleClose();
      } else {
        throw new Error(res.message);
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { borderRadius: "15px", padding: "10px" },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold" color="textPrimary">
          Reorder Therapists Schedule
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Drag and drop to adjust the display order for this day
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ borderTop: "none", overflowY: "auto" }}>
        <Box sx={{ minHeight: "300px", mt: 1 }}>
          <ReactSortable
            list={list}
            setList={setList}
            animation={250}
            ghostClass="sortable-ghost"
            chosenClass="sortable-chosen"
            dragClass="sortable-drag"
            forceFallback={true}
            fallbackClass="sortable-drag"
            delay={10}
            delayOnTouchOnly={false}
            scroll={true}
            scrollSensitivity={100}
            scrollSpeed={10}
            bubbleScroll={true}
          >
            {list.map((item, index) => (
              <Paper
                key={item.id}
                elevation={0}
                sx={{
                  mb: 2,
                  p: 1,
                  border: "1px solid #eef2f6",
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "#f8faff",
                    borderColor: "#2196f3",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  },
                }}
              >
                <ListItem
                  sx={{ cursor: "grab", "&:active": { cursor: "grabbing" } }}
                  secondaryAction={
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontWeight: 500 }}
                    >
                      Position {index + 1}
                    </Typography>
                  }
                >
                  <ListItemIcon sx={{ minWidth: "40px" }}>
                    <DragIndicatorIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.therapistName}
                    primaryTypographyProps={{
                      variant: "subtitle1",
                      fontWeight: "600",
                      color: "#334155",
                    }}
                  />
                </ListItem>
              </Paper>
            ))}
          </ReactSortable>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="text"
          sx={{ color: "#64748b", fontWeight: "bold" }}
        >
          Discard Changes
        </Button>
        <Button
          onClick={handleSaveOrder}
          variant="contained"
          disabled={loading}
          sx={{
            minWidth: "160px",
            height: "45px",
            borderRadius: "10px",
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "bold",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            color: "#fff",
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Save Arrangement"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TherapistReorderModal;
